import { Text, TextArea, Tooltip } from '@radix-ui/themes';
import clsx from 'clsx';
import type { ComponentProps, ReactNode } from 'react';
import { memo, useState } from 'react';
import { HubertButton } from '../HubertButton';
import HubertPopover from '../HubertPopover';
// import { StripHTML } from '@/utils/utils';
// import { nanoid } from '@reduxjs/toolkit';
import { PlateEditor } from 'components/editor/plate-editor';
import type { Value } from 'platejs';
import HubertInput from '../HubertInput';

/**
 * Main/root component
 */
interface HubertPopoverFieldEditProps extends ComponentProps<typeof HubertPopover> {
  children: ReactNode;
}

const HubertPopoverFieldEdit = ({ children, ...props }: HubertPopoverFieldEditProps) => {
  return <HubertPopover {...props}>{children}</HubertPopover>;
};

export default HubertPopoverFieldEdit;

/**
 * Trigger component
 */
interface HubertPopoverFieldEditTriggerProps extends ComponentProps<typeof HubertPopover.Trigger> {
  tooltipContent?: string;
  containerClass?: string;
  truncate?: boolean;
  children: ReactNode;
}

const HubertPopoverFieldEditTrigger = ({
  tooltipContent,
  containerClass,
  truncate = false,
  children,
  ...props
}: HubertPopoverFieldEditTriggerProps) => {
  return (
    <HubertPopover.Trigger {...props} className={clsx(truncate && 'truncate ...')}>
      {tooltipContent ? (
        <Text className={clsx(containerClass, 'cursor-pointer')}>
          <Tooltip content={`${tooltipContent} (Click to edit)`}>{children}</Tooltip>
        </Text>
      ) : (
        children
      )}
    </HubertPopover.Trigger>
  );
};

/**
 * Content component
 */
interface HubertPopoverFieldEditContentProps extends ComponentProps<typeof HubertPopover.Content> {
  editType: 'number' | 'text' | 'textarea' | 'rich-text';
  textAreaProps?: {
    resize: 'none' | 'both' | 'horizontal' | 'vertical' | undefined;
    radius: 'small' | 'none' | 'medium' | 'large' | 'full' | undefined;
    value: string | undefined;
    onChange: (value: string) => void | undefined;
    onBlur: (value: string) => void | undefined;
  };
  min?: number;
  max?: number;
  placeholder?: string;
  onFieldChange?: (stringValue: string, plateValue?: Value) => void;
  onFieldCommit?: (stringValue: string, plateValue?: Value) => void;
  value?: string | number;
  disabled?: boolean;
  children: ReactNode;
}

const HubertPopoverFieldEditContent = memo(function HubertPopoverFieldEditContent({
  editType,
  textAreaProps,
  min,
  max,
  placeholder,
  onFieldChange,
  onFieldCommit,
  value,
  disabled,
  children,
  ...props
}: HubertPopoverFieldEditContentProps) {
  const valueAsString = typeof value === 'string' ? value : value?.toString();
  const [stringValue, setStringValue] = useState<string | undefined>(valueAsString);
  const [plateValue, setPlateValue] = useState<Value>([]);
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const customContainer = document.getElementById('main-content') as HTMLElement;

  if (editType === 'rich-text') {
    return (
      <HubertPopover.Content
        {...props}
        className={clsx('flex flex-col justify-between gap-2', 'min-w-[700px]', 'max-w-[700px]', 'p-[18px]')}
        portalContent={true}
        portalElement={customContainer}
      >
        {children}

        <div className={clsx('flex flex-col gap-2')}>
          <PlateEditor
            value={valueAsString}
            onValueChange={(stringValue, plateValue) => {
              setStringValue(stringValue);
              setPlateValue(plateValue);
              if (onFieldChange) {
                onFieldChange(stringValue, plateValue);
              }
            }}
          />

          {onFieldCommit ? (
            <div className="flex flex-row justify-end gap-2">
              <HubertPopover.Close asChild>
                <HubertButton
                  className="round-r-lg justify-end focus:ring-2"
                  size={'sm'}
                  disabled={disabled || stringValue === value}
                  onClick={() => {
                    if (onFieldCommit && stringValue) {
                      onFieldCommit(stringValue, plateValue);
                    }
                  }}
                >
                  Update
                </HubertButton>
              </HubertPopover.Close>
            </div>
          ) : null}
        </div>
      </HubertPopover.Content>
    );
  }

  return (
    <HubertPopover.Content
      {...props}
      className={clsx('flex flex-col justify-between gap-4', 'p-[18px]')}
      portalContent={true}
      portalElement={customContainer}
    >
      {children}
      <div className={clsx(editType !== 'textarea' && 'flex flex-row gap-0')}>
        {editType === 'textarea' ? (
          <TextArea
            resize={textAreaProps?.resize}
            radius={textAreaProps?.radius}
            value={textAreaProps?.value}
            onChange={(e) => {
              textAreaProps?.onChange(e.target.value);
            }}
            onBlur={(e) => {
              textAreaProps?.onBlur(e.target.value);
            }}
            disabled={disabled}
          />
        ) : (
          <HubertInput
            style={{ borderRadius: '5px 0px 0px 5px' }}
            type={editType}
            min={min}
            max={max}
            placeholder={placeholder ? placeholder : editType === 'number' ? 'Set number' : 'Set text'}
            defaultValue={value}
            onChange={(e) => {
              setStringValue(e.target.value);
              setIsEdited(true);
              if (onFieldChange) {
                onFieldChange(e.target.value);
              }
            }}
            disabled={disabled}
          />
        )}

        {onFieldCommit ? (
          <HubertPopover.Close asChild>
            <HubertButton
              style={{ borderRadius: '0 5px 5px 0' }}
              size={'xl'}
              className="round-r-lg focus:ring-2"
              disabled={disabled || stringValue === value}
              onClick={() => {
                const x = isEdited && stringValue && stringValue !== value;
                if (x) {
                  onFieldCommit(stringValue);
                }
              }}
            >
              Update
            </HubertButton>
          </HubertPopover.Close>
        ) : null}
      </div>
    </HubertPopover.Content>
  );
});

HubertPopoverFieldEdit.Trigger = HubertPopoverFieldEditTrigger;
HubertPopoverFieldEdit.Content = HubertPopoverFieldEditContent;
